/**
 * UserManagementPage - Admin page for managing users and their credits
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Search, MoreHorizontal, Gift, ArrowUpDown } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface UserWithCredits {
  id: string;
  email: string | null;
  full_name: string | null;
  created_at: string;
  credits_left: number | null;
  subscription_status: string | null;
  subscription_tier: string | null;
  trial_end_date: string | null;
  updated_at: string | null;
  total_consumed: number;
}

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdminRole();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"created" | "activity">("created");
  const [selectedUser, setSelectedUser] = useState<UserWithCredits | null>(null);
  const [isGiveCreditsOpen, setIsGiveCreditsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state for give credits
  const [creditsAmount, setCreditsAmount] = useState("");
  const [trialDays, setTrialDays] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");

  // Fetch users with their credits
  const { data: users, isLoading: isUsersLoading, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      // Get profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, full_name, created_at")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Get user credits
      const { data: credits, error: creditsError } = await supabase
        .from("user_credits")
        .select("user_id, credits_left, subscription_status, subscription_tier, trial_end_date, updated_at");

      if (creditsError) throw creditsError;

      // Get consumed credits per user
      const { data: consumed, error: consumedError } = await supabase
        .from("credit_events")
        .select("user_id, delta");

      if (consumedError) throw consumedError;

      // Calculate total consumed per user
      const consumedMap: Record<string, number> = {};
      consumed?.forEach((event) => {
        if (event.delta < 0) {
          consumedMap[event.user_id] = (consumedMap[event.user_id] || 0) + Math.abs(event.delta);
        }
      });

      // Create credits lookup
      const creditsMap: Record<string, typeof credits[0]> = {};
      credits?.forEach((c) => {
        creditsMap[c.user_id] = c;
      });

      // Merge data
      const usersWithCredits: UserWithCredits[] = (profiles || []).map((profile) => {
        const userCredits = creditsMap[profile.id];
        return {
          id: profile.id,
          email: profile.email,
          full_name: profile.full_name,
          created_at: profile.created_at,
          credits_left: userCredits?.credits_left ?? null,
          subscription_status: userCredits?.subscription_status ?? null,
          subscription_tier: userCredits?.subscription_tier ?? null,
          trial_end_date: userCredits?.trial_end_date ?? null,
          updated_at: userCredits?.updated_at ?? null,
          total_consumed: consumedMap[profile.id] || 0,
        };
      });

      return usersWithCredits;
    },
    enabled: isAdmin,
  });

  // Redirect non-admins
  if (!isAdminLoading && !isAdmin) {
    navigate("/", { replace: true });
    return null;
  }

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    let filtered = users;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.email?.toLowerCase().includes(query) ||
          u.full_name?.toLowerCase().includes(query)
      );
    }

    // Sort
    if (sortBy === "activity") {
      filtered = [...filtered].sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return dateB - dateA;
      });
    }

    return filtered;
  }, [users, searchQuery, sortBy]);

  const getStatusBadge = (status: string | null, trialEnd: string | null) => {
    if (!status) return { label: "Unknown", color: "bg-muted text-muted-foreground" };

    const isTrialExpired = trialEnd && new Date(trialEnd) < new Date();

    if (status === "trialing" && isTrialExpired) {
      return { label: "Trial Expired", color: "bg-red-100/80 text-red-700 dark:bg-red-900/30 dark:text-red-400" };
    }

    switch (status) {
      case "trialing":
        return { label: "Trialing", color: "bg-blue-100/80 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" };
      case "active":
        return { label: "Active", color: "bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" };
      case "free":
        return { label: "Free", color: "bg-slate-100/80 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400" };
      case "canceled":
        return { label: "Canceled", color: "bg-amber-100/80 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" };
      default:
        return { label: status, color: "bg-muted text-muted-foreground" };
    }
  };

  const handleGiveCredits = async () => {
    if (!selectedUser) return;

    // Validate credits cannot be lower than current
    const currentCredits = selectedUser.credits_left ?? 0;
    if (creditsAmount && parseInt(creditsAmount, 10) < currentCredits) {
      toast.error(`Credits cannot be lower than current value (${currentCredits})`);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: Record<string, any> = {
        action: "manual_admin_correction",
        userId: selectedUser.id,
      };

      if (creditsAmount) {
        payload.credits = parseInt(creditsAmount, 10);
      }
      if (trialDays) {
        payload.trialDays = parseInt(trialDays, 10);
      }
      if (subscriptionStatus) {
        payload.subscriptionStatus = subscriptionStatus;
      }

      const { data, error } = await supabase.functions.invoke("manage-user-credits", {
        body: payload,
      });

      if (error) throw error;

      toast.success(`Updated credits for ${selectedUser.email || selectedUser.id}`);
      setIsGiveCreditsOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      console.error("Error updating user credits:", error);
      toast.error(error.message || "Failed to update user credits");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCreditsAmount("");
    setTrialDays("");
    setSubscriptionStatus("");
    setSelectedUser(null);
  };

  const openGiveCreditsDialog = (user: UserWithCredits) => {
    setSelectedUser(user);
    setCreditsAmount("");
    setTrialDays("");
    setSubscriptionStatus("");
    setIsGiveCreditsOpen(true);
  };

  // Loading state
  if (isAdminLoading || isUsersLoading) {
    return (
      <AppLayout pageTitle="User Management">
        <div className="min-h-screen bg-background">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <Skeleton className="h-8 w-48 mb-6" />
            <Skeleton className="h-10 w-72 mb-6" />
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="User Management">
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">User Management</h1>
              <p className="text-sm text-muted-foreground">
                {filteredUsers.length} users
              </p>
            </div>
          </div>

          {/* Search and Sort */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setSortBy(sortBy === "created" ? "activity" : "created")}
              className="gap-2"
            >
              <ArrowUpDown className="h-4 w-4" />
              {sortBy === "created" ? "By Created" : "By Activity"}
            </Button>
          </div>

          {/* Users Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[300px]">User</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Credits Left</TableHead>
                  <TableHead className="text-right">Credits Used</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const status = getStatusBadge(user.subscription_status, user.trial_end_date);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-foreground">
                              {user.full_name || "—"}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {user.email || user.id}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Joined {format(new Date(user.created_at), "MMM d, yyyy")}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                          {user.subscription_tier && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({user.subscription_tier})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {user.credits_left ?? "—"}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {user.total_consumed}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openGiveCreditsDialog(user)}>
                                <Gift className="h-4 w-4 mr-2" />
                                Manage Credits
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Give Credits Dialog */}
      <Dialog open={isGiveCreditsOpen} onOpenChange={(open) => {
        setIsGiveCreditsOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage User Credits</DialogTitle>
            <DialogDescription>
              {selectedUser?.email || selectedUser?.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="credits">Set Credits To</Label>
              <Input
                id="credits"
                type="number"
                placeholder={`Minimum: ${selectedUser?.credits_left ?? 0}`}
                min={selectedUser?.credits_left ?? 0}
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Current: {selectedUser?.credits_left ?? 0} credits (can only increase)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="trial-days">Trial Days (extends from now)</Label>
              <Input
                id="trial-days"
                type="number"
                placeholder="e.g., 7"
                min="0"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
              />
            </div>

            {selectedUser?.subscription_status !== "active" && (
              <div className="space-y-2">
                <Label htmlFor="status">Subscription Status</Label>
                <Select value={subscriptionStatus} onValueChange={setSubscriptionStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trialing">Trialing</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only available for users without active subscriptions
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsGiveCreditsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGiveCredits} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default UserManagementPage;
